import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

import Feed from './components/Feed';
import Weather from './components/Weather';
import GoldPrice from './components/GoldPrice';
import Currency from './components/Currency';
import PowerOutage from './components/PowerOutage';
import PrivacyPolicy from "./components/PrivacyPolicy";

import MobileFeed from './routes/MobileFeed';
import MobileUtilityMenu from './routes/MobileUtilityMenu';
import MobileGoldCurrencyScreen from './routes/MobileGoldCurrencyScreen';
import PowerOutageScreen from './routes/PowerOutageScreen';


import useIsMobile from './hooks/useIsMobile';
import { Newspaper, Wrench } from 'lucide-react';

import PhoneLoginPopup from "./components/PhoneLoginPopup";
import UserProfilePopup from "./components/UserProfilePopup";
import CreatePostPopup from "./components/CreatePostPopup";
import { auth, db } from "./firebase";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import Favorites from './components/Favorites';
import PostDetail from "./routes/PostDetail";

import branch from 'branch-sdk';
import NotificationButton from './components/NotificationButton';
import OutageLookup from './routes/OutageLookup';
import ContactBox from './components/ContactBox';
import ChristmasDecorations from './components/ChristmasDecorations';

branch.init('key_live_aztgDSMYw96qPOj4SnHpeldlEDlK07wY');

function App() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [currentUser, setCurrentUser] = useState(null);
  const [firestoreUser, setFirestoreUser] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [showFavorites, setShowFavorites] = useState(false);
  const menuRef = useRef(null);

  const [showAppBanner, setShowAppBanner] = useState(false);

  // ✅ Hiển thị banner khi mở bằng mobile
  useEffect(() => {
    const isMobileDevice = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const dismissed = localStorage.getItem("appBannerDismissed");
    if (isMobileDevice && !dismissed) {
      setTimeout(() => setShowAppBanner(true), 1000);
    }
  }, []);

  const handleDismissBanner = () => {
    setShowAppBanner(false);
    localStorage.setItem("appBannerDismissed", "true");
  };

  const handleOpenApp = () => {
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    const isAndroid = /Android/i.test(navigator.userAgent);

    const appScheme = "nhontrachtoday://"; // deep link scheme
    const appStoreLink = "https://apps.apple.com/app/id6748644977"; // App Store
    const playStoreLink = "https://play.google.com/store/apps/details?id=com.hnc.nhontrachtoday"; // 🔹 thay bằng package thật

    // Thử mở app trước
    window.location.href = appScheme;

    // Nếu không có app, fallback sau 1.5s
    setTimeout(() => {
      if (isIOS) {
        window.location.href = appStoreLink;
      } else if (isAndroid) {
        window.location.href = playStoreLink;
      } else {
        // fallback desktop
        window.location.href = "https://nhontrach-today.web.app";
      }
    }, 1500);
  };

  // Firebase Auth
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCurrentUser(user);
        const q = query(collection(db, "users"), where("phone", "==", user.phoneNumber));
        const unsubscribeUser = onSnapshot(q, (snap) => {
          if (!snap.empty) {
            setFirestoreUser({ id: snap.docs[0].id, ...snap.docs[0].data() });
          }
        });
        return () => unsubscribeUser();
      } else {
        setCurrentUser(null);
        setFirestoreUser(null);
      }
    });
    return () => unsubscribe();
  }, []);

  // Đóng menu khi click ra ngoài
  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="flex flex-col min-h-screen">
      <ChristmasDecorations />
      <header
        className="shadow-lg shadow-halloween-purple/40 px-4 py-2 flex items-center justify-between sticky top-0 z-50 border-b-2 border-halloween-orange/60"
        style={{
          background:
            'linear-gradient(120deg, #0F3D2E 0%, #14532D 52%, #176B3A 76%, #B4232F 100%)',
        }}
      >
        <div className="flex items-center gap-3">
          {isMobile && (
            <button className="md:hidden" onClick={() => setDrawerOpen(true)}>
              <Menu className="w-6 h-6 text-white" />
            </button>
          )}

          <h1
            className="text-2xl font-bold flex items-center gap-3 cursor-pointer text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]"
            onClick={() => (window.location.href = "/")}
          >
            <img src="/icons/logo.png?v=2025" alt="Nhơn Trạch" className="w-12 h-12 rounded-xl ring-2 ring-halloween-orange/80" />
            {isMobile ? "" : (
              <>
                Nhơn Trạch Hôm Nay
                <span className="text-lg font-normal text-halloween-cream/90 hidden lg:inline" aria-hidden>
                  🎄
                </span>
              </>
            )}
          </h1>

          {/* ⭐ BUTTONS moved here - next to the logo ⭐ */}
          <div className="hidden md:flex items-center gap-2">
            <a
              href="https://apps.apple.com/us/app/nhontrach-today/id6748644977"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:opacity-80"
            >
              <img
                src="/icons/appstore.png"
                alt="Download on the App Store"
                className="h-10 object-contain"
              />
            </a>

            <a
              href="https://play.google.com/store/apps/details?id=com.hnc.nhontrachtoday"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:opacity-80"
            >
              <img
                src="/icons/googleplay.png"
                alt="Get it on Google Play"
                className="h-10 object-contain"
              />
            </a>
          </div>
        </div>

        {/* =================== RIGHT AREA =================== */}
        <div className="relative flex items-center gap-4">
          {currentUser && firestoreUser ? (
            <>
              <NotificationButton />

              {firestoreUser.type === 1 && (
                <button
                  className="bg-white text-election-primary font-bold hover:opacity-90 px-3 py-1 rounded"
                  onClick={() => setShowCreatePost(true)}
                >
                  +{isMobile ? "" : " Bài viết mới"}
                </button>
              )}

              <div
                className="flex items-center gap-2 cursor-pointer text-white"
                onClick={() => setShowUserMenu((prev) => !prev)}
              >
                <img
                  src={
                    firestoreUser.avatarUrl ||
                    "https://ui-avatars.com/api/?name=User&background=random"
                  }
                  alt="avatar"
                  className="w-8 h-8 rounded-full border-2 border-white"
                />
                <span className="font-medium text-white">{firestoreUser.name}</span>
              </div>

              {showUserMenu && (
                <div
                  ref={menuRef}
                  className="absolute right-0 top-full mt-2 w-40 bg-white shadow-lg rounded-md overflow-hidden z-50 text-gray-800"
                >
                  <button
                    onClick={() => {
                      setShowProfile(true);
                      setShowUserMenu(false);
                    }}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100"
                  >
                    Xem hồ sơ
                  </button>

                  <button
                    onClick={() => {
                      setShowFavorites(true);
                      setShowUserMenu(false);
                    }}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100"
                  >
                    Đã quan tâm
                  </button>

                  <button
                    onClick={() => {
                      signOut(auth);
                      setShowUserMenu(false);
                    }}
                    className="w-full text-left px-4 py-2 text-vietnam-primary hover:bg-gray-100"
                  >
                    Đăng xuất
                  </button>
                </div>
              )}

              {showProfile && (
                <UserProfilePopup onClose={() => setShowProfile(false)} />
              )}
              {showCreatePost && (
                <CreatePostPopup onClose={() => setShowCreatePost(false)} />
              )}
            </>
          ) : (
            <>
              <button
                onClick={() => setShowLogin(true)}
                className="px-6 py-2 bg-gradient-to-r from-halloween-orange to-halloween-purple-dark text-white font-bold rounded-full shadow-lg shadow-halloween-orange/30 hover:shadow-xl hover:scale-105 transition-all duration-300 ease-in-out border border-halloween-orange/50"
              >
                Đăng nhập
              </button>

              {showLogin && (
                <PhoneLoginPopup onClose={() => setShowLogin(false)} />
              )}
            </>
          )}
        </div>
      </header>


      {/* Drawer */}
      {isMobile && drawerOpen && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-40 md:hidden">
          <div className="fixed left-0 top-0 w-72 h-full bg-halloween-cream shadow-lg p-4 overflow-y-auto border-r-2 border-halloween-orange/40">
            <div className="flex justify-between items-center mb-4">
              <button onClick={() => setDrawerOpen(false)}>
                <X className="w-6 h-6 text-gray-700" />
              </button>
            </div>
            <div className="mb-4">
              <Weather />
            </div>
            <nav className="space-y-3">
              <button
                onClick={() => { navigate('/'); setDrawerOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-3 bg-gray-100 hover:bg-vietnam-light border border-gray-200 rounded-lg shadow-sm transition-all"
              >
                <Newspaper className="w-5 h-5 text-vietnam-primary" />
                <span className="text-sm font-medium text-gray-800">Bảng tin</span>
              </button>

              <button
                onClick={() => { navigate('/utilities'); setDrawerOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-3 bg-gray-100 hover:bg-vietnam-light border border-gray-200 rounded-lg shadow-sm transition-all"
              >
                <Wrench className="w-5 h-5 text-vietnam-primary" />
                <span className="text-sm font-medium text-gray-800">Tiện ích</span>
              </button>
            </nav>
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1">
        {isMobile ? (
          <Routes>
            <Route path="/" element={<MobileFeed />} />
            <Route path="/utilities" element={<MobileUtilityMenu />} />
            <Route path="/utilities/mobile-gold-currency" element={<MobileGoldCurrencyScreen />} />
            <Route path="/utilities/mobile-power" element={<PowerOutageScreen />} />
            <Route path="/post/:postId/:focus" element={<PostDetail />} />
            <Route path="/outage_check" element={<OutageLookup />} />
          </Routes>
        ) : (
          <div className="hidden md:grid grid-cols-12 gap-4 p-4">
            <div className="col-span-3 space-y-4">
              <GoldPrice />
              <Currency />
            </div>
            <div className="col-span-6 pb-[100px]">
              <Routes>
                <Route path="/" element={showFavorites ? <Favorites /> : <Feed />} />
                <Route path="/post/:postId/:focus" element={<PostDetail />} />
                <Route path="/outage_check" element={<OutageLookup />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
              </Routes>
            </div>
            <div className="col-span-3 space-y-4">
              <Weather />
              <PowerOutage />
              <ContactBox />
            </div>
          </div>
        )}
      </div>

      {/* ✅ Banner tải app giống Zalo */}
      {showAppBanner && (
        <div className="fixed bottom-3 left-3 right-3 bg-halloween-cream border-2 border-halloween-orange/50 shadow-lg shadow-halloween-purple/20 rounded-xl p-3 flex items-center justify-between z-50 animate-slide-up">
          <div className="flex items-center gap-3">
            <img src="/icons/logo.png" alt="App icon" className="w-12 h-12 rounded-xl" />
            <div>
              <p className="font-semibold text-gray-800">Tải ứng dụng Nhơn Trạch Today</p>
              <p className="text-sm text-gray-500">Cập nhật tin tức nhanh hơn, trải nghiệm mượt mà hơn!</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleOpenApp}
              className="bg-vietnam-primary text-white text-sm px-3 py-2 rounded-lg shadow hover:bg-vietnam-dark"
            >
              Mở app
            </button>
            <button onClick={handleDismissBanner} className="text-gray-400 hover:text-black text-lg">✕</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
